import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button as MuiButton, Box } from '@mui/material';
import { FileTextOutlined, DownloadOutlined, CheckCircleOutlined, LoginOutlined } from '@ant-design/icons';
import { TermsLink } from '../components/TermsLink';

// Shared style objects
const containerStyles = {
  height: '100%',
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  WebkitOverflowScrolling: 'touch',
  background: 'linear-gradient(180deg, #f5f5f5 0%, #ffffff 100%)',
  display: 'flex',
  flexDirection: 'column'
};

const topSectionStyles = {
  background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #FF6B35 100%)',
  padding: 'clamp(30px, 4vh, 60px) clamp(20px, 3vw, 40px) clamp(20px, 3vh, 40px) clamp(20px, 3vw, 40px)',
  textAlign: 'center',
  color: '#fff',
  flex: '0 0 auto',
  position: 'relative',
  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  minHeight: 'fit-content'
};

const logoContainerStyles = (fadeIn) => ({
  position: 'absolute',
  top: 'clamp(10px, 2vh, 20px)',
  left: 'clamp(10px, 3vw, 40px)',
  opacity: fadeIn ? 1 : 0,
  transition: 'opacity 0.8s ease-out'
});

const logoStyles = {
  height: 'clamp(60px, 8vh, 100px)',
  width: 'auto',
  maxWidth: 'clamp(200px, 30vw, 400px)'
};

const titleContainerStyles = (fadeIn) => ({
  maxWidth: '1000px',
  margin: '0 auto',
  opacity: fadeIn ? 1 : 0,
  transform: fadeIn ? 'translateY(0)' : 'translateY(-20px)',
  transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
});

const titleStyles = {
  fontSize: 'clamp(28px, 5vw, 56px)',
  fontWeight: '700',
  color: '#fff',
  margin: '0 0 clamp(10px, 2vh, 20px) 0',
  textShadow: '2px 2px 8px rgba(0,0,0,0.2)',
  letterSpacing: '-0.5px',
  lineHeight: '1.2'
};

const subtitleStyles = {
  fontSize: 'clamp(14px, 2.2vw, 22px)',
  color: '#fff',
  margin: '0',
  lineHeight: '1.5',
  opacity: 0.95
};

const contentSectionStyles = {
  flex: '1 1 auto',
  background: 'linear-gradient(180deg, #ffffff 0%, #f8f8f8 100%)',
  padding: 'clamp(20px, 3vh, 40px) clamp(15px, 2vw, 20px)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center'
};

const contentWrapperStyles = {
  maxWidth: '1000px',
  width: '100%',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  flex: '1 1 auto',
  minHeight: 0
};

const featuresGridStyles = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(200px, 25vw, 300px), 1fr))',
  gap: 'clamp(15px, 2vh, 25px)',
  marginBottom: 'clamp(20px, 3vh, 35px)',
  width: '100%'
};

const cardBaseStyles = {
  background: '#ffffff',
  padding: 'clamp(15px, 2vh, 25px)',
  borderRadius: '16px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
  border: '1px solid #e0e0e0',
  cursor: 'pointer'
};

const iconStyles = {
  fontSize: 'clamp(30px, 4vw, 40px)',
  color: '#FF6B35',
  marginBottom: 'clamp(10px, 1.5vh, 15px)'
};

const cardTitleStyles = {
  color: '#1a1a1a',
  marginBottom: 'clamp(8px, 1vh, 10px)',
  fontSize: 'clamp(16px, 2vw, 18px)',
  fontWeight: '600'
};

const cardDescStyles = {
  color: '#4a4a4a',
  fontSize: 'clamp(12px, 1.5vw, 14px)',
  lineHeight: '1.6',
  margin: '0'
};

const freePlanStyles = (fadeIn) => ({
  background: 'linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 50%, #FF6B35 100%)',
  color: '#fff',
  padding: 'clamp(15px, 2vh, 25px)',
  borderRadius: '16px',
  marginBottom: 'clamp(20px, 3vh, 35px)',
  boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
  border: '1px solid rgba(255, 107, 53, 0.3)',
  opacity: fadeIn ? 1 : 0,
  transform: fadeIn ? 'translateY(0)' : 'translateY(20px)',
  transition: 'opacity 0.8s ease-out 0.8s, transform 0.8s ease-out 0.8s',
  width: '100%'
});

const freePlanTitleStyles = {
  margin: '0 0 clamp(8px, 1.5vh, 12px) 0',
  fontSize: 'clamp(18px, 3vw, 24px)',
  fontWeight: '600',
  color: '#fff'
};

const freePlanDescStyles = {
  margin: '0',
  fontSize: 'clamp(14px, 2vw, 16px)',
  lineHeight: '1.5',
  color: '#fff'
};

const buttonContainerStyles = (fadeIn) => ({
  display: 'flex',
  justifyContent: 'center',
  opacity: fadeIn ? 1 : 0,
  transform: fadeIn ? 'translateY(0)' : 'translateY(20px)',
  transition: 'opacity 0.8s ease-out 1s, transform 0.8s ease-out 1s'
});

const buttonSx = {
  height: 'clamp(45px, 6vh, 50px)',
  minWidth: 'clamp(180px, 25vw, 220px)',
  padding: '0 clamp(25px, 4vw, 35px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  fontSize: 'clamp(14px, 2vw, 16px)',
  fontWeight: '600',
  textTransform: 'none',
  borderRadius: '8px',
  background: 'linear-gradient(135deg, #FF6B35 0%, #E55A2B 100%)',
  boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'linear-gradient(135deg, #E55A2B 0%, #FF6B35 100%)',
    boxShadow: '0 6px 16px rgba(255, 107, 53, 0.4)',
    transform: 'translateY(-2px)'
  },
  '&:active': {
    transform: 'translateY(0)'
  }
};

// Feature Card Component
const FeatureCard = ({ Icon, titleKey, descKey, delay, fadeIn, t }) => {
  const baseTransform = fadeIn ? 'translateY(0)' : 'translateY(20px)';
  
  return (
    <Box
      sx={{
        ...cardBaseStyles,
        opacity: fadeIn ? 1 : 0,
        transform: baseTransform,
        transition: `opacity 0.8s ease-out ${delay}, transform 0.8s ease-out ${delay}, all 0.3s cubic-bezier(0.4, 0, 0.2, 1)`,
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
          borderColor: '#FF6B35'
        }
      }}
    >
      <Icon style={iconStyles} />
      <h3 style={cardTitleStyles}>{t(titleKey)}</h3>
      <p style={cardDescStyles}>{t(descKey)}</p>
    </Box>
  );
};

export const LandingPage = () => {
  const { t } = useTranslation('translation');
  const history = useHistory();
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    setTimeout(() => setFadeIn(true), 100);
  }, []);

  const handleSignIn = () => {
    history.push('/login');
  };

  const features = [
    { Icon: FileTextOutlined, titleKey: 'landing-feature-1-title', descKey: 'landing-feature-1-desc', delay: '0.2s' },
    { Icon: DownloadOutlined, titleKey: 'landing-feature-2-title', descKey: 'landing-feature-2-desc', delay: '0.4s' },
    { Icon: CheckCircleOutlined, titleKey: 'landing-feature-3-title', descKey: 'landing-feature-3-desc', delay: '0.6s' }
  ];

  return (
    <div style={{ ...containerStyles, direction: t('direction') }}>
      {/* Top Orange Section */}
      <div style={topSectionStyles}>
        {/* Logo */}
        <div style={logoContainerStyles(fadeIn)}>
          <img src="/icon clear-01.png" alt="Logo" style={logoStyles} />
        </div>

        <div style={titleContainerStyles(fadeIn)}>
          <h1 style={titleStyles}>{t('landing-title')}</h1>
          <p style={subtitleStyles}>{t('landing-subtitle')}</p>
        </div>
      </div>

      {/* White Content Section */}
      <div style={contentSectionStyles}>
        <div style={contentWrapperStyles}>
          {/* Features */}
          <div style={featuresGridStyles}>
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                Icon={feature.Icon}
                titleKey={feature.titleKey}
                descKey={feature.descKey}
                delay={feature.delay}
                fadeIn={fadeIn}
                t={t}
              />
            ))}
          </div>

          {/* Free Plan Info */}
          <div style={freePlanStyles(fadeIn)}>
            <h2 style={freePlanTitleStyles}>{t('landing-free-plan-title')}</h2>
            <p style={freePlanDescStyles}>{t('landing-free-plan-desc')}</p>
          </div>

          {/* Action Button */}
          <div style={buttonContainerStyles(fadeIn)}>
            <MuiButton onClick={handleSignIn} variant="contained" size="large" sx={buttonSx}>
              {t('landing-sign-in-up-button')}
              <LoginOutlined style={{ fontSize: '18px' }} />
            </MuiButton>
          </div>

          {/* Footer with Terms popup link */}
          <div style={{ marginTop: '32px', paddingBottom: '24px', textAlign: 'center' }}>
            <TermsLink style={{ fontSize: '14px' }} />
          </div>
        </div>
      </div>
    </div>
  );
};

